import { useContext, useEffect, useState } from "react";
import { Box, CircularProgress, Container, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";

import type {
  FormContextData,
  PatientTimelineResponse,
} from "@thalamos/aspire-common";

import { soadAssessmentLogic } from "../FormProgressPage/soad-assessment/soadAssessmentLogic.js";
import { apiHooks } from "~/api.js";
import { Button, renderErrorToast } from "~/components/design-system/index.js";
import { FormFooterSection, TrainingBanner } from "~/components/form/index.js";
import { PatientBanner } from "~/components/layout/index.js";
import { MultiPagePdf } from "~/components/MultiPagePdf.js";
import { LoggedInUserContext } from "~/Contexts.js";
import { usePatientTimeline } from "~/hooks/apiCalls.js";
import { routeFns } from "~/routePaths.js";

type SoadSubmitReviewPageInnerProps = {
  formContext: FormContextData;
  patientTimeline: PatientTimelineResponse;
  reloadPatientTimeline: () => Promise<void>;
  reloadFormContext: () => Promise<void>;
};

function SoadSubmitReviewPageInner({
  formContext,
  patientTimeline,
  reloadPatientTimeline,
}: SoadSubmitReviewPageInnerProps) {
  const userContext = useContext(LoggedInUserContext);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const {
    canSave,
    saveAction,
    certificates: { forms },
    soadReportForm: { form },
  } = soadAssessmentLogic(
    formContext,
    userContext!.user,
    navigate,
    formContext?.activeTeamworkWorkItem !== undefined,
  );

  const formsToPreview = [form, ...forms.map((f) => f.form)];

  const [formIndex, setFormIndex] = useState<number>(0);

  const [{ data: pdfData, loading }] = apiHooks.soad.getPdf(
    formContext.id,
    formsToPreview[formIndex]?.id ?? "",
  );

  useEffect(() => {
    if (!canSave || saveAction !== "review") {
      renderErrorToast({
        message: "SOAD assessment is not ready to be submitted",
      });
      navigate(routeFns.formContextPage(formContext.id, formContext.patientId));
    }
  }, []);

  return (
    <>
      <Stack
        sx={{
          gap: 1,
          mb: 3,
        }}
      >
        <TrainingBanner />
        <PatientBanner
          patient={formContext.patient}
          nhsNumber={patientTimeline.patient.nhsNumber ?? undefined}
          disableSticky
          isHorizontalLineHidden
          patientTimeline={patientTimeline}
          reloadPatientTimeline={reloadPatientTimeline}
          isHidden={true}
        />
      </Stack>
      <Container
        sx={{
          display: "flex",
          alignItems: "center",
          flexDirection: "column",
          width: "200px",
          minHeight: "750px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            mb: 2,
            width: "750px",
          }}
        >
          <Button
            label="Previous"
            disabled={formIndex === 0}
            onClick={() => {
              setFormIndex((prev) => prev - 1);
            }}
          />
          <Button
            label="Next"
            disabled={formIndex === formsToPreview.length - 1}
            onClick={() => {
              setFormIndex((prev) => prev + 1);
            }}
          />
        </Box>
        {loading || !pdfData ? (
          <Box>
            <CircularProgress />
          </Box>
        ) : (
          <MultiPagePdf data={pdfData} />
        )}
      </Container>
      <FormFooterSection
        hideSubmit={false}
        isSticky={true}
        onSave={() => {
          navigate(
            routeFns.SoadSignAndSendPage(formContext.id, formContext.patientId),
          );
        }}
        saveLabel={t("buttonLabels.sign")}
        discardLabel={t("buttonLabels.close")}
        onCancel={() => {
          navigate(
            routeFns.formContextPage(formContext.id, formContext.patientId),
          );
        }}
      />
    </>
  );
}

export type soadSubmitReviewPageProps = Record<string, never>;

export function SoadSubmitReviewPage(_props: soadSubmitReviewPageProps) {
  const { formContextId, patientId } = useParams();
  const navigate = useNavigate();

  const [
    { data: form, loading: formLoading, response: formResponse },
    reloadFormContext,
  ] = apiHooks.forms.getFormContext(formContextId!);

  useEffect(() => {
    if (!formLoading) {
      if (formResponse?.status === 404) {
        navigate(routeFns.notFound(), { replace: true });
      }
    }
  }, [form, formLoading, formResponse?.status]);

  const {
    patientTimeline,
    reloadPatientTimeline,
    patientTimelineCompleteLoading,
  } = usePatientTimeline({
    patientId,
  });

  return formLoading ||
    !form ||
    patientTimelineCompleteLoading ||
    !patientTimeline ? (
    <CircularProgress />
  ) : (
    <SoadSubmitReviewPageInner
      formContext={form}
      reloadFormContext={async () => {
        reloadFormContext();
      }}
      patientTimeline={patientTimeline}
      reloadPatientTimeline={async () => {
        reloadPatientTimeline();
      }}
    />
  );
}
